import { Inject, Injectable } from "@angular/core";
import { CommonLibraryEnvironment, PerformWorkflowUserAction, WorkflowItemDto } from "./models";
import { LIB_ENVIRONMENT } from "./environment.token";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { WalletOnboardingResponse } from "../models/wallet-onboarding.interface";
import { IAutoOnboarding, IAutoOnboardingBackfillResult, IAvsrRerunResult, ICipcRerunResult, IIdvRerunResult, IKycRerunResult, IPendingVerificationItem, IVerificationCheck, IVerificationDecisionRequest, IVerificationResponse } from "../models/verification.interface";
import { IContactCorrectionRequest, IContactCorrectionResult, IContactCorrectionState } from "../models/contact-correction.interface";

@Injectable({
    providedIn: 'root'
  })
  /**
   * HTTP client for the Loans API workflow endpoints. Powers the backoffice workflow
   * screens: pending item lists, user actions (approve/decline), resets, cancellations
   * and merchant verification/onboarding detail retrieval.
   */
  export class WorkflowService {

    /** Base URL of the workflow API for the current environment. */
    get baseUrl(): string {
        return this.env.apiUrl + "/api/v1/workflow";
    }

    constructor(
        @Inject(LIB_ENVIRONMENT) private env: CommonLibraryEnvironment,
        private http: HttpClient) {}

    /**
     * Performs a user action (e.g. approve/decline) on a workflow item.
     * @param id Workflow item identifier.
     * @param action Action payload to apply.
     * @returns Observable of the updated workflow item.
     */
    performUserAction(id: string, action: PerformWorkflowUserAction): Observable<WorkflowItemDto> {
      return this.http.post<WorkflowItemDto>(`${this.baseUrl}/${id}/action`, action);
    }

    /**
     * Finds wallet onboarding items by the merchant's ID number, full or leading digits (work item 904).
     * Results carry the masked ID only.
     * @param idNumber Six or more digits of the ID number.
     */
    searchByIdNumber(idNumber: string): Observable<WorkflowItemDto[]> {
      return this.http.get<WorkflowItemDto[]>(`${this.baseUrl}/search`, { params: { idNumber } });
    }

    /**
     * Cancels a workflow item.
     * @param id Workflow item identifier.
     * @returns Observable of the cancelled workflow item.
     */
    cancelWorkflowItem(id: string): Observable<WorkflowItemDto> {
      return this.http.delete<WorkflowItemDto>(`${this.baseUrl}/${id}`);
    }

    /**
     * Gets a single workflow item.
     * @param id Workflow item identifier.
     * @returns Observable of the workflow item.
     */
    getWorkflowItem(id: string): Observable<WorkflowItemDto> {
      return this.http.get<WorkflowItemDto>(`${this.baseUrl}/${id}`);
    }

    /**
     * Lists workflow items for a given workflow definition name.
     * @param definition Workflow definition name.
     * @returns Observable of matching workflow items.
     */
    getWorkflowsByWorkflowName(definition: string): Observable<WorkflowItemDto[]> {
      return this.http.get<WorkflowItemDto[]>(`${this.baseUrl}/bydefinition/${definition}`);
    }

    /**
     * Lists workflow items assigned to the current user.
     * @returns Observable of the user's workflow items.
     */
    getWorkflowItemsByUsername(): Observable<WorkflowItemDto[]> {
      return this.http.get<WorkflowItemDto[]>(this.baseUrl);
    }

    /**
     * Lists workflow items in a given group.
     * @param group Workflow group name.
     * @returns Observable of the group's workflow items.
     */
    getWorkflowItemsByGroup(group: string): Observable<WorkflowItemDto[]> {
      return this.http.get<WorkflowItemDto[]>(`${this.baseUrl}/groups/${group}`);
    }

    /**
     * Lists all workflow group names.
     * @returns Observable of group names.
     */
    getWorkflowGroups(): Observable<string[]> {
      return this.http.get<string[]>(`${this.baseUrl}/groups`);
    }

    /**
     * Resets a failed/stuck workflow item so it can be reprocessed.
     * @param id Workflow item identifier.
     * @returns Observable of the reset workflow item.
     */
    resetWorkflowItem(id: string): Observable<WorkflowItemDto> {
      return this.http.post<WorkflowItemDto>(`${this.baseUrl}/${id}/Reset`, {});
    }

    /**
     * Gets the IDV/KYC/AVS-R/CIPC verification results for a workflow item.
     * @param id Workflow item identifier.
     * @returns Observable of the verification response, or undefined when not available.
     */
    public getVerificationDetails(id: string): Observable<IVerificationResponse | undefined> {
      return this.http.get<IVerificationResponse | undefined>(`${this.baseUrl}/${id}/verification-details`);
    }

    /**
     * Assesses a wallet onboarding waiting at Compliance Verification for auto-onboarding, or assesses
     * it again. Never approves or moves it (work item 956). The API rejects it (400) at any other step.
     * @param id Workflow item identifier.
     */
    public assessAutoOnboarding(id: string): Observable<IAutoOnboarding> {
      return this.http.post<IAutoOnboarding>(`${this.baseUrl}/${id}/assess-auto-onboarding`, {});
    }

    /** Assesses every wallet onboarding waiting at Compliance Verification that has no assessment yet (work item 956). */
    public assessWaitingAutoOnboarding(): Observable<IAutoOnboardingBackfillResult> {
      return this.http.post<IAutoOnboardingBackfillResult>(`${this.baseUrl}/auto-onboarding/assess-waiting`, {});
    }

    /**
     * Re-runs the SlicPay KYC check for a wallet onboarding whose KYC step was bypassed during a
     * provider outage. The API rejects the call (400) when KYC was never bypassed.
     * @param id Workflow item identifier.
     */
    public rerunKyc(id: string): Observable<IKycRerunResult> {
      return this.http.post<IKycRerunResult>(`${this.baseUrl}/${id}/rerun-kyc`, {});
    }

    /**
     * Re-runs the SlicPay IDV check for a wallet onboarding whose ID step was bypassed during a
     * provider outage. The API rejects the call (400) when IDV was never bypassed.
     * @param id Workflow item identifier.
     */
    public rerunIdv(id: string): Observable<IIdvRerunResult> {
      return this.http.post<IIdvRerunResult>(`${this.baseUrl}/${id}/rerun-idv`, {});
    }

    /**
     * Re-runs the SlicPay CIPC company check for a wallet onboarding whose CI step was bypassed
     * during a provider outage. The API rejects the call (400) when CIPC was never bypassed.
     * @param id Workflow item identifier.
     */
    public rerunCipc(id: string): Observable<ICipcRerunResult> {
      return this.http.post<ICipcRerunResult>(`${this.baseUrl}/${id}/rerun-cipc`, {});
    }

    /**
     * Re-runs AVSR for an onboarding at Compliance Verification whose AVSR did not pass (work item 897).
     * A paid SlicPay call.
     */
    public rerunAvsr(id: string): Observable<IAvsrRerunResult> {
      return this.http.post<IAvsrRerunResult>(`${this.baseUrl}/${id}/rerun-avsr`, {});
    }

    /**
     * Records Compliance's decision on a failed AVSR or KYC (work items 757, 897). Accepted lets the
     * onboarding be approved; Rejected keeps approval blocked. Audited with who, when and why.
     */
    public recordVerificationDecision(id: string, request: IVerificationDecisionRequest): Observable<IVerificationCheck> {
      return this.http.post<IVerificationCheck>(`${this.baseUrl}/${id}/verification-decision`, request);
    }

    /**
     * Fetches a provider report or reply for a workflow item through the authorised endpoint
     * (work item 653), so the bearer token goes with it; never a public blob URL.
     */
    public getVerificationDocument(id: string, kind: string): Observable<Blob> {
      return this.http.get(`${this.baseUrl}/${id}/verification-documents/${encodeURIComponent(kind)}`, { responseType: 'blob' });
    }

    /**
     * Gets the "Fix and retry" panel state for a wallet onboarding workflow item: current email and
     * cellphone, what may be corrected, a plain-language diagnosis and past corrections.
     * @param id Workflow item identifier.
     */
    public getContactCorrection(id: string): Observable<IContactCorrectionState> {
      return this.http.get<IContactCorrectionState>(`${this.baseUrl}/${id}/contact-correction`);
    }

    /**
     * Corrects the email and/or cellphone on a faulted wallet onboarding, with a mandatory reason.
     * With `retry: true` the API also resets the fault so the failed step runs again.
     * @param id Workflow item identifier.
     * @param request New values, reason and whether to retry.
     */
    public correctContactDetails(id: string, request: IContactCorrectionRequest): Observable<IContactCorrectionResult> {
      return this.http.post<IContactCorrectionResult>(`${this.baseUrl}/${id}/contact-correction`, request);
    }

    /**
     * Lists wallet onboardings still waiting for a bypassed KYC/IDV/CIPC check to be re-run, in any
     * workflow status, oldest bypass first.
     */
    public getPendingVerification(): Observable<IPendingVerificationItem[]> {
      return this.http.get<IPendingVerificationItem[]>(`${this.baseUrl}/pending-verification`);
    }

    /**
     * Gets the saved wallet-onboarding draft linked to a workflow item.
     * @param id Workflow item identifier.
     * @returns Observable of the onboarding response, or undefined when not available.
     */
    public getSavedOnboarding(id: string): Observable<WalletOnboardingResponse | undefined> {

      return this.http.get<WalletOnboardingResponse | undefined>(`${this.env.apiUrl}/api/v1/walletonboarding/${id}`);
    }
  }
